import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Paper,
  Button,
  Text,
  Group,
  Stack,
  Card,
  Badge,
  RingProgress,
  Grid,
  Divider,
  ThemeIcon,
  Loader,
  Alert,
  Box
} from '@mantine/core';
import {
  IconTrophy,
  IconCards,
  IconBrain,
  IconChartBar,
  IconRefresh,
  IconDashboard,
  IconInfoCircle
} from '@tabler/icons-react';
import { flashcardService } from '../services/flashcardService';
import { FlashcardEstadisticasDTO } from '../Models/Flashcard';

const FlashcardsResumenPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estadisticas, setEstadisticas] = useState<FlashcardEstadisticasDTO | null>(null);

  // Parámetros de la sesión
  const totalFlashcards = parseInt(searchParams.get('totalFlashcards') || '0');
  const flashcardsEstudiadas = parseInt(searchParams.get('flashcardsEstudiadas') || '0');
  const usuarioId = parseInt(searchParams.get('usuarioId') || '0');
  const especialidadId = searchParams.get('especialidadId') ? parseInt(searchParams.get('especialidadId')!) : undefined;

  // Cargar estadísticas actualizadas
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const stats = await flashcardService.obtenerEstadisticas(usuarioId, especialidadId);
        setEstadisticas(stats);
      } catch (error) {
        console.error('Error al cargar estadísticas:', error);
        setError('Error al cargar las estadísticas.');
      } finally {
        setLoading(false);
      }
    };

    if (usuarioId > 0) {
      loadStats();
    } else {
      setError('Error: Parámetros de sesión inválidos');
      setLoading(false);
    }
  }, [usuarioId, especialidadId]);

  // Calcular métricas de la sesión
  const porcentajeEstudiadas = totalFlashcards > 0 ? (flashcardsEstudiadas / totalFlashcards) * 100 : 0;
  const progresoGeneral = estadisticas?.porcentajeCompletado || 0;

  // Obtener mensaje de felicitación
  const getMensajeFelicitacion = () => {
    if (porcentajeEstudiadas === 100) {
      return '¡Excelente! Completaste toda la sesión';
    } else if (porcentajeEstudiadas >= 75) {
      return '¡Muy bien! Gran progreso en tu estudio';
    } else if (porcentajeEstudiadas >= 50) {
      return '¡Buen trabajo! Sigue así';
    } else if (porcentajeEstudiadas > 0) {
      return '¡Bien hecho! Cada flashcard cuenta';
    } else {
      return 'Sesión completada';
    }
  };

  // Navegación
  const handleEstudiarMas = () => {
    navigate('/estudiante/flashcards');
  };

  const handleVerEstadisticas = () => {
    navigate('/estudiante/flashcards/estadisticas');
  };

  const handleVolverDashboard = () => {
    navigate('/estudiante/dashboard');
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Cargando resumen...</Text>
        </Group>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconInfoCircle size="1rem" />} title="Error" color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        {/* Header de celebración */}
        <Paper shadow="none" radius="lg" p="lg" bg="gradient-to-r from-blue-50 to-green-50">
          <Group justify="space-between" align="flex-start" mb="sm">
            <Box style={{ flex: 1 }}>
              <Title order={2} size="h3" mb="xs" ta="left">
                ¡Sesión Completada!
              </Title>
              
              <Text size="md" c="dimmed" mb="sm" ta="left">
                {getMensajeFelicitacion()}
              </Text>
              
              <Text size="sm" fw={500} ta="left">
                Estudiaste {flashcardsEstudiadas} flashcards en esta sesión
              </Text>
            </Box>
            
            <ThemeIcon size={40} radius="md" variant="light" color="yellow">
              <IconTrophy size={20} />
            </ThemeIcon>
          </Group>
        </Paper>

        {/* Estadísticas de la sesión */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Card shadow="none" radius="md" p="lg" h="100%">
              <Group justify="space-between" mb="md">
                <Text fw={500}>Sesión Actual</Text>
                <IconCards size={20} style={{ opacity: 0.6 }} />
              </Group>
              
              <Group justify="center" mb="md">
                <RingProgress
                  size={100}
                  thickness={10}
                  sections={[
                    { value: porcentajeEstudiadas, color: '#8B6F47' }
                  ]}
                  label={
                    <Text ta="center" fw={700} size="md">
                      {flashcardsEstudiadas}
                    </Text>
                  }
                />
              </Group>
              
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Total en sesión:</Text>
                  <Badge variant="light">{totalFlashcards}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Estudiadas:</Text>
                  <Badge variant="light" color="green">{flashcardsEstudiadas}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Completado:</Text>
                  <Badge variant="filled" style={{ backgroundColor: '#8B6F47' }}>
                    {porcentajeEstudiadas.toFixed(1)}%
                  </Badge>
                </Group>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Card shadow="none" radius="md" p="lg" h="100%">
              <Group justify="space-between" mb="md">
                <Text fw={500}>Progreso General</Text>
                <IconChartBar size={20} style={{ opacity: 0.6 }} />
              </Group>
              
              <Group justify="center" mb="md">
                <RingProgress
                  size={100}
                  thickness={10}
                  sections={[
                    { value: progresoGeneral, color: 'green' }
                  ]}
                  label={
                    <Text ta="center" fw={700} size="md">
                      {progresoGeneral.toFixed(0)}%
                    </Text>
                  }
                />
              </Group>
              
              {estadisticas && (
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm">Total general:</Text>
                    <Badge variant="light">{estadisticas.totalFlashcards}</Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Estudiadas:</Text>
                    <Badge variant="light" color="green">{estadisticas.flashcardsEstudiadas}</Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Pendientes:</Text>
                    <Badge variant="light" color="orange">{estadisticas.flashcardsPendientes}</Badge>
                  </Group>
                </Stack>
              )}
            </Card>
          </Grid.Col>
        </Grid>

        <Divider />

        {/* Botones de navegación */}
        <Stack gap="md">
          <Button
            size="md"
            leftSection={<IconRefresh size={18} />}
            onClick={handleEstudiarMas}
            fullWidth
            styles={{
              root: {
                backgroundColor: '#8B6F47',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#735a38',
                },
              },
            }}
          >
            Estudiar Más
          </Button>
        </Stack>

      </Stack>
    </Container>
  );
};

export default FlashcardsResumenPage;